<template>
    <div class="devices-live-map" :style="{ height: height, position:'relative' }">
        <l-map ref="map" v-model:zoom="zoom" :center="mapCenter" @ready="mapIsReady" @click="onMapClick">
            <l-tile-layer :key="mapType" :url="currentBasemap.url"
                          :subdomains="currentBasemap.subdomains"/>

            <!-- Recent-movement breadcrumb path (last 10 min): a black core with a
                 white 2px border and a black 2px border (three stacked strokes),
                 drawn right above the tiles and under the markers (Leaflet's
                 overlay pane sits below the marker pane). Shows where each tracker
                 just travelled. All outers, then whites, then cores, so crossings
                 read black|white|black uniformly. -->
            <template v-for="line in recentTrailLines" :key="`rp-o-${line.id}`">
                <l-polyline :lat-lngs="line.points" color="#000000" :weight="11" :opacity="0.55"/>
            </template>
            <template v-for="line in recentTrailLines" :key="`rp-w-${line.id}`">
                <l-polyline :lat-lngs="line.points" color="#ffffff" :weight="7" :opacity="0.9"/>
            </template>
            <template v-for="line in recentTrailLines" :key="`rp-c-${line.id}`">
                <l-polyline :lat-lngs="line.points" color="#000000" :weight="3" :opacity="1"/>
            </template>

            <!-- Movement trail polylines -->
            <l-polyline v-for="trail in activeTrails" :key="trail.id"
                        :lat-lngs="trail.points"
                        :color="trail.color"
                        :weight="3"
                        :opacity="trail.opacity"
                        :dash-array="trail.fading ? '5,8' : null"
            />

            <!-- Device markers (devices folded into a cluster are hidden; the
                 cluster badge below stands in for them) -->
            <l-marker
                v-for="device in unclusteredDevices"
                :key="device.id"
                ref="markers"
                :name="`marker-${device.id}`"
                :lat-lng="[device.lat ?? 0, device.lng ?? 0]"
                :z-index-offset="markerZ(device)"
                @click="selectMarker(device)"
                @popupopen="onPopupOpen(device)"
                @popupclose="onPopupClose(device)"
            >
                <l-icon :iconSize="[46, 60]" :iconAnchor="[23, 55]" :popupAnchor="[0, -52]" class-name="marker">
                    <SubjectMarker :device="device"
                                   :tracked="trackedDeviceIds.has(device.id)"
                                   :highlighted="!!device.isHighlighted"
                                   :offset="markerOffset(device)"/>
                </l-icon>
                <l-popup :options="{ maxWidth: 320, minWidth: 300 }">
                    <div class="popup-container">
                        <!-- Header -->
                        <div class="popup-header">
                            <div class="popup-photo-wrapper">
                                <v-avatar size="40" rounded="lg" class="popup-photo">
                                    <v-img v-if="device.asset_photo" :src="device.asset_photo" cover/>
                                    <v-img v-else-if="device.asset_type_photo" :src="device.asset_type_photo" cover/>
                                    <v-icon v-else :icon="subjectIcon(device)" size="24" color="grey"/>
                                </v-avatar>
                                <v-avatar v-if="device.driver_photo" size="20" class="popup-driver-overlay">
                                    <v-img :src="device.driver_photo" :lazy-src="avatarPlaceholder" cover/>
                                </v-avatar>
                            </div>
                            <div class="popup-header-info">
                                <div class="popup-asset-label">{{ device.asset_label || device.slno || 'Tracker' }}</div>
                                <div class="popup-subtitle">{{ device.acquired_vin || device.vin || device.slno }}</div>
                            </div>
                            <div class="popup-header-right">
                                <v-badge v-if="device.unread_notifications" dot color="orange" offset-x="-2"
                                         offset-y="-2">
                                    <v-icon icon="mdi-bell-outline" size="18" color="grey"/>
                                </v-badge>
                            </div>
                        </div>

                        <!-- Status strip with compass direction -->
                        <div class="popup-status-strip">
                            <span class="popup-motion-pill" :style="{background: statusPill(device).color}">
                                <v-icon :icon="statusPill(device).icon" size="12" color="white"/>
                                {{ statusPill(device).label }}
                            </span>
                            <span v-if="device.motion_state && (device.gps_speed_raw ?? device.gps_speed) > 2"
                                  class="popup-speed">
                                {{ strOperations().unitifySpeed(device) }}
                                <span class="popup-direction">{{ compassLabel(device.heading) }}</span>
                            </span>
                            <span class="popup-status-icons">
                                <v-icon v-bind="battery(device)" size="14"/>
                                <v-icon v-if="isLowBatteryDevice(device)" icon="mdi-battery-alert-variant" size="14"
                                        color="#d70000" :title="lowBatteryHint(device)"/>
                                <v-icon v-bind="signals(device.signal_bars??0)" size="14"/>
                                <v-icon v-if="obdStatus(device).show" icon="mdi-engine" size="14"
                                        color="#F97316" :title="obdHint(device)"/>
                                <!-- Fuel: only an OBD vehicle ever reports a tank
                                     level, and the server sends null rather than 0
                                     when the car never answered, so the presence of
                                     a value is the whole gate. `!== null` and not a
                                     truthiness test, or an empty tank would vanish. -->
                                <span v-if="fuelLevel(device) !== null" class="popup-fuel"
                                      :style="{color: fuelColor(fuelLevel(device))}"
                                      :title="fuelHint(device)">
                                    <v-icon icon="mdi-gas-station" size="14"/>{{ fuelLevel(device) }}%
                                </span>
                                <span class="popup-voltage">{{ batteryText(device) }}</span>
                            </span>
                        </div>

                        <!-- Driver row -->
                        <div v-if="device.driver_first_name" class="popup-info-row">
                            <v-icon icon="mdi-account" size="14" color="grey" class="flex-shrink-0"/>
                            <span class="text-truncate" style="min-width: 0">{{ device.driver_first_name }} {{ device.driver_last_name }}</span>
                            <v-chip v-if="device.driver_score != null" size="x-small" variant="flat" class="ms-1 flex-shrink-0"
                                    :color="driverScoreColor(device.driver_score)">
                                {{ device.driver_score }}
                            </v-chip>
                        </div>

                        <!-- Location: one block - saved Safe Zone name ("At Home")
                             as the primary line when inside one, street address
                             beneath, a single icon and the copy/open actions.
                             Outside any zone it's just the address. -->
                        <div class="popup-info-row popup-location-row">
                            <v-icon :icon="isInZone(device) ? 'mdi-map-marker-radius' : 'mdi-map-marker'"
                                    size="14" :color="isInZone(device) ? 'green' : 'grey'" class="flex-shrink-0 mt-1"/>
                            <span class="popup-location-text">
                                <span v-if="isInZone(device)" class="popup-zone-name">At {{ device.current_zone }}</span>
                                <span v-if="addressText(device)" class="popup-location-address">{{ addressText(device) }}</span>
                                <!-- Only "unavailable" when there is genuinely no fix. A fresh, moving
                                     device can have valid coords but no address yet (zone cleared on
                                     motion + geocode throttled), so gate on coordinates, not the
                                     address string, else a live device is mislabelled unavailable. -->
                                <span v-else-if="!isInZone(device) && !deviceLatLng(device)" class="popup-location-address">Location unavailable</span>
                                <span v-if="presenceText(device)" class="popup-presence">
                                    <v-icon icon="mdi-clock-outline" size="11"/> {{ presenceText(device) }}
                                </span>
                            </span>
                            <div class="popup-location-actions">
                                <v-icon icon="mdi-content-copy" size="14" class="popup-inline-icon"
                                        title="Copy Google Maps link" @click="copyText(device)"/>
                                <v-icon icon="mdi-open-in-new" size="14" class="popup-inline-icon"
                                        title="Open in Google Maps" @click="openInGoogleMaps(device)"/>
                            </div>
                        </div>

                        <!-- Last comm / heartbeat (any packet, incl. stationary) -->
                        <div class="popup-info-row">
                            <v-icon icon="mdi-access-point" size="14" :color="freshnessColor(device)"/>
                            <span>Last heard {{ tickingTimeAgo(device.last_message_received) }}</span>
                        </div>

                        <!-- Phone out-of-charge notice: a phone that is low needs a
                             recharge or it will stop reporting. Only for phones (a
                             vehicle's low battery shows via the status-strip icon). -->
                        <div v-if="isPhoneDevice(device) && isLowBatteryDevice(device)" class="popup-info-row"
                             style="color: #d70000">
                            <v-icon icon="mdi-battery-alert-variant" size="14" color="#d70000" class="flex-shrink-0"/>
                            <span>Low battery - phone may stop updating until charged</span>
                        </div>

                        <v-divider class="my-2"/>

                        <!-- Recent trips (last 3 days) with focus-to-replay -->
                        <PopupRecentTrips :device="device" :active="openPopupId === device.id"/>

                        <v-divider class="my-2"/>

                        <!-- Action buttons -->
                        <div class="popup-actions">
                            <div class="popup-action-btn" @click="openDeviceActivity(device)">
                                <span class="popup-action-circle" style="background: #00695C">
                                    <v-icon icon="mdi-timeline-clock" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Activity</span>
                            </div>
                            <!-- People can be called or messaged via their linked contact -->
                            <template v-if="subjectKindOf(device) === 'person' && device.driver_phone">
                                <div class="popup-action-btn" @click="callContact(device)">
                                    <span class="popup-action-circle" style="background: #2E7D32">
                                        <v-icon icon="mdi-phone" size="18" color="white"/>
                                    </span>
                                    <span class="popup-action-label">Call</span>
                                </div>
                                <div class="popup-action-btn" @click="messageContact(device)">
                                    <span class="popup-action-circle" style="background: #1565C0">
                                        <v-icon icon="mdi-message-text" size="18" color="white"/>
                                    </span>
                                    <span class="popup-action-label">Message</span>
                                </div>
                            </template>
                            <div class="popup-action-btn"
                                 @click="toggleFollow(device)">
                                <span class="popup-action-circle"
                                      :style="{background: trackedDeviceIds.has(device.id) ? '#9E9E9E' : '#7B1FA2'}">
                                    <v-icon
                                        :icon="trackedDeviceIds.has(device.id) ? 'mdi-crosshairs-off' : 'mdi-crosshairs-gps'"
                                        size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">{{
                                        trackedDeviceIds.has(device.id) ? 'Unfollow' : 'Follow'
                                    }}</span>
                            </div>
                            <div class="popup-action-btn" @click="openStreetView(device)">
                                <span class="popup-action-circle" style="background: #2E7D32">
                                    <v-icon icon="mdi-google-street-view" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Street View</span>
                            </div>
                            <div v-if="(device.lat ?? device.last_known_lat) != null && (device.lng ?? device.last_known_lng) != null"
                                 class="popup-action-btn"
                                 @click="saveAsPlace(device)">
                                <span class="popup-action-circle" style="background: #00897B">
                                    <v-icon icon="mdi-map-marker-plus" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Save Place</span>
                            </div>
                            <div class="popup-action-btn" @click="openShare(device)">
                                <span class="popup-action-circle" style="background: #5E35B1">
                                    <v-icon icon="mdi-share-variant" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Share</span>
                            </div>
                            <div class="popup-action-btn" @click="openInGoogleMaps(device)">
                                <span class="popup-action-circle" style="background: #1565C0">
                                    <v-icon icon="mdi-google-maps" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Google Maps</span>
                            </div>
                            <div v-if="device.unread_notifications" class="popup-action-btn"
                                 @click="openDeviceAlerts()">
                                <span class="popup-action-circle" style="background: #EF6C00">
                                    <v-icon icon="mdi-bell" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Alerts</span>
                            </div>
                        </div>

                        <!-- Engine diagnostics (MIL + DTC) - vehicles with a fault only -->
                        <DeviceObdSection :device="device" :active="openPopupId === device.id"/>

                        <!-- Hardware serial (tap to copy) - physical trackers only -->
                        <div class="popup-info-row" v-if="device.slno && !isPhoneDevice(device)">
                            <v-icon icon="mdi-barcode" size="14"/>
                            <span>Serial: {{ device.slno }}</span>
                            <v-icon icon="mdi-content-copy" size="14" class="popup-inline-icon"
                                    style="margin-left: auto;" title="Copy serial"
                                    @click="copyToClipboard(String(device.slno))"/>
                        </div>

                        <!-- VIN (tap to copy) - vehicles -->
                        <div class="popup-info-row" v-if="device.acquired_vin || device.vin">
                            <v-icon icon="mdi-card-account-details-outline" size="14"/>
                            <span>VIN: {{ device.acquired_vin || device.vin }}</span>
                            <v-icon icon="mdi-content-copy" size="14" class="popup-inline-icon"
                                    style="margin-left: auto;" title="Copy VIN"
                                    @click="copyToClipboard(device.acquired_vin || device.vin)"/>
                        </div>

                        <!-- Battery voltage - hardware trackers only. A phone reports a
                             PERCENT on voltage1 (percent = mv / 50), which the battery
                             chip above already shows, so a "0.24v" row there would be
                             nonsense. `device.voltage` is the MONITORED channel, picked
                             from the SKU's battery1_status/battery2_status when the MQTT
                             update lands, and battery1_label/battery2_label name it -
                             "External Battery" on an OBD, not a generic "Voltage". -->
                        <div class="popup-info-row" v-if="!isPhoneDevice(device) && popupVoltage(device)">
                            <v-icon icon="mdi-flash" size="14"/>
                            <span>{{ popupVoltageLabel(device) }}: {{ popupVoltage(device) }}</span>
                        </div>
                    </div>
                </l-popup>
            </l-marker>

            <!-- Address labels for followed devices -->
            <l-marker v-for="device in followedDevicesWithPosition" :key="'label-' + device.id"
                      :lat-lng="[device.lat, device.lng]"
                      :options="{ interactive: false }">
                <l-icon :iconSize="[200, 0]" :iconAnchor="[100, -22]" class-name="address-label-anchor">
                    <div class="address-label">
                        <div class="address-label-name">{{ device.asset_label || device.slno || 'Tracker' }}</div>
                        <div class="address-label-info">
                            <span v-if="subjectStatusOf(device) === 'moving'"
                                  class="address-label-speed moving">
                                {{ strOperations().unitifySpeed(device) }} {{ compassLabel(device.heading) }}
                            </span>
                            <!-- Arrival (movement ended), not last comm: "Arrived 31m ago" -->
                            <span v-else class="address-label-speed">{{ arrivalText(device) }}</span>
                        </div>
                        <div class="address-label-address">
                            <v-icon v-if="isInZone(device)" icon="mdi-home-map-marker" size="11"
                                    class="address-label-place-ic"/>
                            {{ placeText(device) ? truncateAddress(placeText(device)) : (deviceLatLng(device) ? '' : 'Location unavailable') }}
                        </div>
                    </div>
                </l-icon>
            </l-marker>

            <!-- Cluster badges: a dense group of markers collapses into one count
                 badge at the group centroid; tap to break it apart (zoom in). -->
            <l-marker
                v-for="cluster in clusters"
                :key="cluster.id"
                :lat-lng="[cluster.lat, cluster.lng]"
                :z-index-offset="950"
                @click="onClusterClick(cluster)"
            >
                <l-icon :iconSize="[46, 46]" :iconAnchor="[23, 23]" class-name="cluster-marker">
                    <div class="cluster-badge">{{ cluster.count }}</div>
                </l-icon>
            </l-marker>
        </l-map>

        <!-- Basemap selector (Map / Satellite) -->
        <div class="basemap-control">
            <v-btn-toggle :model-value="mapType" mandatory density="comfortable" rounded="lg"
                          color="primary" variant="elevated" @update:model-value="setMapType">
                <v-btn v-for="(b, key) in BASEMAPS" :key="key" :value="key" size="small">
                    <v-icon :icon="b.icon" size="18" start/>
                    {{ b.label }}
                </v-btn>
            </v-btn-toggle>
        </div>

        <!-- Following panel -->
        <div v-if="trackedDeviceIds.size > 0" class="following-panel">
            <v-chip color="purple" variant="flat" size="small" class="cursor-pointer"
                    @click="showFollowingPanel = !showFollowingPanel">
                <v-icon start size="14">mdi-crosshairs-gps</v-icon>
                Following: {{ trackedDeviceIds.size }}
                <v-icon end size="14">{{ showFollowingPanel ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </v-chip>
            <div v-if="showFollowingPanel" class="following-panel-list">
                <div v-for="device in followedDevicesWithPosition" :key="'fp-' + device.id"
                     class="following-panel-item">
                    <span class="following-panel-name">{{ device.asset_label || device.slno || 'Tracker' }}</span>
                    <v-btn icon="mdi-close" size="x-small" variant="text" density="compact"
                           @click="unfollowDevice(device.id)"/>
                </div>
                <v-btn v-if="trackedDeviceIds.size > 1" block variant="text" size="x-small" color="error"
                       @click="unfollowAll">Unfollow All
                </v-btn>
            </div>
        </div>

        <!-- Torch beams -->
        <TorchBeamLayer :devices="devicesInMotion" :is-map-ready="mapState" :map-ref="map"/>

        <!-- Share live location (dialog teleports to body) -->
        <ShareLocationDialog v-if="shareDevice" :key="shareDevice.id"
                             :device-id="shareDevice.id" :name="shareDevice.asset_label"
                             @close="shareDevice = null"/>

        <!-- Right-click "Save place here" context menu -->
        <div v-if="contextMenu.show" class="map-context-menu"
             :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }">
            <button class="map-context-item" @click="onSavePlaceHere">
                <v-icon size="16" class="me-1">mdi-map-marker-plus-outline</v-icon>
                Save place here
            </button>
        </div>

        <!-- Create a place seeded at the right-clicked point -->
        <PlaceDialog v-if="placeDialogOpen" v-model="placeDialogOpen"
                     :initial-point="placePoint" @placeCreated="onPlaceCreatedFromMap"/>
    </div>
</template>

<script setup>
import L from "leaflet";
import {mapConfig} from "@/composables/mapConfig";
import "leaflet/dist/leaflet.css";
import {send} from "@/composables/apiClient";
import {strOperations} from "@/composables/globals";

// --- Marker-popup battery voltage (hardware trackers only) ---
//
// `device.voltage` is already the MONITORED channel: the MQTT handler below
// picks voltage2 when the SKU sets battery2_status without battery1_status,
// which on an Orion OBD is the 12V vehicle rail rather than the 2.8V supercap.
// Name it from the SKU too (battery1_label/battery2_label, exposed by
// DeviceLiveViewResource) so the row reads "External Battery: 11.31v" instead
// of a generic "Voltage". Falls back when a SKU leaves its labels unset.
const popupVoltage = (device) => {
    // Select the channel here too, not just in the MQTT merge below. The API
    // sends `voltage` as voltage1 unconditionally, so on an Orion OBD - where
    // battery2 is the 12V vehicle rail and voltage1 is the ~2.8V supercap -
    // this row read "External Battery: 2.72v" for a car whose battery was fine
    // at 12.54V, until a live packet happened to correct it.
    //
    // Fixed here rather than in the resource: both mobile apps read that field
    // as literally voltage1 and render it beside voltage2 under the two battery
    // labels, so changing the API would print one number twice.
    const usesBat2 = device?.battery2_status && !device?.battery1_status;
    const raw = usesBat2 ? (device?.voltage2 ?? device?.voltage) : device?.voltage;
    const mv = parseFloat(raw);
    return (!isNaN(mv) && mv > 0) ? strOperations().formatVoltage(mv) : null;
};

const popupVoltageLabel = (device) => {
    const usesBat2 = device?.battery2_status && !device?.battery1_status;
    const raw = usesBat2 ? device?.battery2_label : device?.battery1_label;
    const s = (raw ?? '').toString().trim();
    return s !== '' ? s : 'Battery';
};
import {useClipboard} from "@/composables/clipboard";
import {batteryDisplay, isPhoneDevice, isLowBatteryDevice, lowBatteryHint, vehicleBatteryPercent} from "@/composables/usePowerSourceIcon";
import {obdStatus, obdHint} from "@/composables/useObdStatus";
import {fuelLevel, fuelColor, fuelHint} from "@/composables/useFuelLevel";
import PopupRecentTrips from "./PopupRecentTrips.vue";
import DeviceObdSection from "./DeviceObdSection.vue";
import {geocodeLatLng, isValidAddress} from "@/composables/useGeocode";
import avatarPlaceholder from "@/assets/images/avatar.png";
import {computed, inject, nextTick, onBeforeUnmount, reactive, ref, watch} from "vue";
import {useRouter} from "vue-router";
import TorchBeamLayer from "@/components/cards/devices/TorchBeamLayer.vue";
import SubjectMarker from "@/components/SubjectMarker.vue";
import ShareLocationDialog from "@/components/dialogs/ShareLocationDialog.vue";
import PlaceDialog from "@/components/dialogs/PlaceDialog.vue";
import {subjectIcon, subjectKindOf, subjectStatusOf, statusBadge, statusMeta, freshness} from "@/composables/subjectKind";
import {NON_REPORTING_HOURS} from "@/composables/fleetStatus";
import {LIcon, LMap, LMarker, LPolyline, LPopup, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {computeMarkerOffsets} from "./markerCollision.mjs";
import {sameSerial} from '@/composables/deviceSerial';

// Popup status pill from the shared, accessible status badge (same source as the
// device list + map marker) so all three agree - including Offline, which the
// old motion-only logic could not show.
const statusPill = (device) => statusBadge(device);
// Freshness tint for the "Updated Xm ago" clock icon (live/stale/offline).
const freshnessColor = (device) => statusMeta(freshness(device)).color;

// Friendly spelled-out dwell for the presence line: "2 mins", "1 hr 5 mins",
// "3 days". '' when the timestamp is absent/unparseable. Ticks with the 5s timer.
// Rounds a sub-minute duration up to 1 min so it always reads as a real dwell.
const durationWords = (raw) => {
    void timeAgoTick.value;
    if (!raw) return '';
    let s = String(raw).trim();
    if (!s) return '';
    s = s.replace(' ', 'T');
    if (!s.endsWith('Z') && !/[+-]\d\d:?\d\d$/.test(s)) s += 'Z';
    const diffMs = Date.now() - new Date(s).getTime();
    if (isNaN(diffMs) || diffMs < 0) return '';
    const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
    const mins = Math.max(1, Math.floor(diffMs / 60000));
    if (mins < 60) return plural(mins, 'min');
    const hours = Math.floor(mins / 60);
    const rm = mins % 60;
    if (hours < 24) return rm > 0 ? `${plural(hours, 'hr')} ${plural(rm, 'min')}` : plural(hours, 'hr');
    const days = Math.floor(hours / 24);
    const rh = hours % 24;
    return rh > 0 ? `${plural(days, 'day')} ${plural(rh, 'hr')}` : plural(days, 'day');
};
// Life360-style presence: how long they've been HERE (since motion ended) or
// MOVING (since motion started), shown next to the location so the popup leads
// with dwell, not a bare status word. Offline shows nothing here - the "Last
// heard" row below already owns that same last_message_received time.
const presenceText = (device) => {
    const st = subjectStatusOf(device);
    if (st === 'moving') {
        const d = durationWords(device?.motion_started_at);
        return d ? `Moving for ${d}` : '';
    }
    if (st === 'idling') {
        const d = durationWords(device?.motion_started_at);
        return d ? `Idling for ${d}` : '';
    }
    if (st === 'live') {
        const d = durationWords(device?.motion_ended_at);
        return d ? `Here for ${d}` : '';
    }
    return '';
};
// Street address line beneath the zone name in the location block.
const addressText = (device) => isApproximate(device)
    ? 'Approximate location, waiting for first GPS fix'
    : (device?.location || device?.formatted_address || '');

// ===== Presence (where + when they arrived) vs last comm (heartbeat) =====
// Arrival = when movement ended (motion_ended_at): captured live from the MQTT
// MOTION_ENDED message, and on load from the API's device_statuses.motion_ended_at.
// NOTE: do NOT fall back to location_updated - that column is a boolean flag
// ("is the location fresh"), not a timestamp. Distinct from last_message_received,
// which keeps advancing on every stationary heartbeat. When we have no arrival
// time yet, the label/popup degrade to the status word (no wrong time shown).
const arrivalAgo = (device) => {
    void timeAgoTick.value; // recompute on the 5s ticker even when the device object is static
    return getTimeAgo(device?.motion_ended_at);
};
// Place resolves to a saved Safe Zone name when the backend matched one
// (device.current_zone), otherwise the street address. "if available" fallback;
// degrades cleanly to the address before the API field ships.
const placeText = (device) => isApproximate(device)
    ? 'Approximate location, waiting for first GPS fix'
    : (device?.current_zone || device?.location || device?.formatted_address || '');
// Never claim zone presence for an approximate seed: it has no real position, so
// "At <zone>" would be false. The API already nulls current_zone for an
// approximate device; this guards the client against that contract regressing.
const isInZone = (device) => !isApproximate(device) && !!device?.current_zone;
// One-line presence for the followed-device label: arrival when stopped,
// last-seen when offline (speed/heading is shown separately while moving).
const arrivalText = (device) => {
    void timeAgoTick.value; // refresh on the ticker (silent/offline devices don't mutate)
    if (subjectStatusOf(device) === 'offline') {
        const seen = getTimeAgo(device?.last_message_received);
        return seen ? `Last seen ${seen}` : 'Offline';
    }
    const ago = arrivalAgo(device);
    return ago ? `Arrived ${ago}` : statusPill(device).label;
};
const router = useRouter();

// Which device's popup is currently open, so its Recent-trips section only
// fetches when actually shown (not once per marker on the map).
const openPopupId = ref(null);
const onPopupOpen = (device) => { openPopupId.value = device.id; };
const onPopupClose = (device) => { if (openPopupId.value === device.id) openPopupId.value = null; };

const map = ref();
const zoom = ref(2);
const bounds = ref([]);
const markers = ref([]);
const mapState = ref([]);
const selectedMarker = ref(null);
// Marker stacking priority (PDF section K): the selected marker is always on
// top, then alerts, then moving, then idling/live, with offline last - so an
// important marker is never hidden behind a lower-priority one.
const markerZ = (device) => {
    if (selectedMarker.value && device.id === selectedMarker.value.id) return 1000;
    if (device.unread_notifications) return 800;
    switch (subjectStatusOf(device)) {
        case 'moving':
            return 600;
        case 'idling':
            return 400;
        case 'live':
            return 300;
        default:
            return 100; // offline
    }
};

// ===== Collision declutter + clustering (Dynamic Marker guide, slices 2-3) =====
// Screen-space pass that groups nearby markers: members sharing one spot fan out
// in a full ring around it (each leader pointing back to the shared point, so
// none sits dead-centre); merely-crowded but distinct markers nudge their
// lower-priority members apart (each with a leader back to its own true point);
// large dense groups can collapse into a single count badge (disabled here).
// Safe by construction: any error or a disabled flag clears all offsets/clusters
// so markers fall back to normal pins (no regression).
const COLLISION_ENABLED = true;
// Clustering (count badges) is OFF for this consumer app: a family expects to
// always see each tracker, so collapsing them into a count badge when zoomed out
// reads as "my trackers disappeared". Crowded markers instead fan out via
// computeMarkerOffsets (markerCollision.mjs), so every tracker stays visible at
// any zoom; nothing is ever hidden behind a badge.
const CLUSTER_ENABLED = false;
const COLLISION_MIN_DIST = 52; // px between marker centres = "crowded"
const ZERO_OFFSET = {x: 0, y: 0};
const markerOffsets = ref({});
const clusters = ref([]);            // [{id, lat, lng, count, memberIds}]
const clusteredIds = ref(new Set()); // device ids hidden inside a cluster
// Spiderfy (Slice 4): the device ids currently fanned out from a tapped cluster.
// Tracked by device id (not the cluster-id string) so selecting a fanned member
// doesn't collapse the fan. Empty = nothing spiderfied. Cleared on map move/zoom
// and outside click. Groups larger than SPIDERFY_MAX zoom in instead of fanning.
const spiderfiedIds = ref(new Set());
const SPIDERFY_MAX = 12;
const markerOffset = (device) => (COLLISION_ENABLED && markerOffsets.value[device.id]) || ZERO_OFFSET;
// A device only earns a map pin once it has a real fix. Guards the [0,0] fallback
// in the l-marker lat-lng: a device that has never reported (the server sends no
// coordinates for one) would otherwise pin to the Gulf of Guinea. Such devices
// still appear in the list, badged "Not yet reporting".
// Placeholder coordinates that are not a real position: the old (34,-84) and the
// activation default (central US, mirrors the API's services.default_location). A
// device sitting on one earns no pin, so an approximate seed only shows at a
// meaningful resolved location (not the central-US fallback).
const isDefaultCoord = (lat, lng) => {
    if (Math.abs(lat - 34.0) < 0.001 && Math.abs(lng + 84.0) < 0.001) return true;
    return Math.abs(lat - 38.736040) < 0.0005 && Math.abs(lng + 100.274368) < 0.0005;
};
const hasPosition = (d) => {
    const lat = Number(d?.lat);
    const lng = Number(d?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng)
        && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
        && !(lat === 0 && lng === 0)
        && !isDefaultCoord(lat, lng);
};
// A just-activated device shown at its approximate seed position (flagged by the
// API under ?approximate=1), not a real GPS fix. Raw Postgres booleans can arrive
// as 't'/1, so read loosely. The pin is drawn faded until the first real fix.
const isApproximate = (d) => {
    const v = d?.approximate;
    return v === true || v === 1 || v === '1' || v === 't' || v === 'true';
};
// Whether the map may draw this device at all. hidden_on_map is a per-tracker
// display preference: it keeps reporting and keeps its list row, it just gets no
// pin. Everything that moves the camera below filters through this too, or the
// map would fit-to-bounds around a marker nobody can see.
const isMappable = (d) => hasPosition(d) && !d?.hidden_on_map;
// The pins actually drawn: mappable devices, minus any folded into a cluster badge.
const unclusteredDevices = computed(() => {
    const located = (props.devices || []).filter(isMappable);
    return CLUSTER_ENABLED ? located.filter((d) => !clusteredIds.value.has(d.id)) : located;
});

let collideTimer = null;
const scheduleCollision = () => {
    if (collideTimer) clearTimeout(collideTimer);
    collideTimer = setTimeout(recomputeCollisions, 150);
};
// Fan the given members out in a ring around their screen centroid: each gets a
// radial offset so its callout body sits on the ring with the pointer leading
// back to its true GPS point (reuses the SubjectMarker offset/leader mechanism).
const spiderfyGroup = (members, offsets) => {
    const n = members.length;
    if (!n) return;
    const cx = members.reduce((s, m) => s + m.x, 0) / n;
    const cy = members.reduce((s, m) => s + m.y, 0) / n;
    const R = Math.min(150, 46 + n * 9); // ring grows with count, capped
    members.forEach((m, i) => {
        const ang = (i / n) * 2 * Math.PI - Math.PI / 2; // start at the top
        const tx = cx + Math.cos(ang) * R;
        const ty = cy + Math.sin(ang) * R;
        offsets[m.id] = {x: Math.round(tx - m.ox), y: Math.round(ty - m.oy)};
    });
};
const recomputeCollisions = () => {
    if (!COLLISION_ENABLED) {
        markerOffsets.value = {};
        clusters.value = [];
        clusteredIds.value = new Set();
        return;
    }
    const lmap = map.value?.leafletObject;
    if (!lmap) return;
    try {
        const list = [];
        (props.devices || []).forEach((d) => {
            // Only devices that actually get a pin take part in the declutter,
            // or a hidden one would push visible markers off their true points.
            if (!isMappable(d)) return;
            const pt = lmap.latLngToContainerPoint([d.lat, d.lng]);
            // Moving devices stay at the true point (bulb over the torch beam).
            const moving = subjectStatusOf(d) === 'moving';
            list.push({id: d.id, lat: d.lat, lng: d.lng, x: pt.x, y: pt.y, ox: pt.x, oy: pt.y, priority: markerZ(d), moving});
        });
        // Connected groups by screen proximity (BFS over <COLLISION_MIN_DIST).
        const n = list.length;
        const seen = new Array(n).fill(false);
        const offsets = {};
        const newClusters = [];
        const newClustered = new Set();
        // Spiderfy: fan the chosen cluster's members in a ring and take them out
        // of normal grouping (mark seen) so they don't re-cluster while expanded.
        // If fewer than 2 remain on screen, the cluster is gone - stop spiderfy.
        const spider = spiderfiedIds.value;
        if (spider.size) {
            const members = list.filter((m) => spider.has(m.id));
            if (members.length >= 2) {
                spiderfyGroup(members, offsets);
                for (let i = 0; i < n; i++) if (spider.has(list[i].id)) seen[i] = true;
            } else {
                spiderfiedIds.value = new Set();
            }
        }
        // Stable declutter (markerCollision.mjs, a port of the app's engine):
        // each crowded group fans out around its anchor by TRUE bearing, so a
        // pan/zoom keeps every marker on its side instead of reshuffling. Feed it
        // only the markers not already fanned by an open spiderfy. Anchored =
        // moving (bulb over the torch beam) OR followed (pulsing-ring pin) - those
        // stay on their true point and don't take a fan slot.
        const inputs = [];
        for (let i = 0; i < n; i++) {
            if (seen[i]) continue; // spiderfied members are pre-fanned above
            const m = list[i];
            inputs.push({
                id: m.id,
                x: m.x,
                y: m.y,
                priority: m.priority,
                anchored: m.moving || trackedDeviceIds.has(m.id),
            });
        }
        Object.assign(offsets, computeMarkerOffsets(inputs, {minDist: COLLISION_MIN_DIST, maxOffset: 80}));
        markerOffsets.value = offsets;
        clusters.value = newClusters;
        clusteredIds.value = newClustered;
    } catch (e) {
        markerOffsets.value = {}; // fall back to plain pins
        clusters.value = [];
        clusteredIds.value = new Set();
    }
};
// Tap a cluster: zoom in so the group breaks apart on the next recompute.
const expandCluster = (cluster) => {
    const lmap = map.value?.leafletObject;
    if (!lmap) return;
    lmap.setView([cluster.lat, cluster.lng], Math.min(lmap.getZoom() + 2, lmap.getMaxZoom?.() || 18), {animate: true});
};
// Tap a cluster badge: a small group fans out (spiderfy) in place so you can see
// and tap each member; a large group zooms in instead (a ring of 30 is useless).
const onClusterClick = (cluster) => {
    if (cluster.count <= SPIDERFY_MAX && Array.isArray(cluster.memberIds)) {
        spiderfiedIds.value = new Set(cluster.memberIds);
        // Recompute now for instant feedback; cancel any pending debounced run.
        if (collideTimer) clearTimeout(collideTimer);
        recomputeCollisions();
    } else {
        expandCluster(cluster);
    }
};
// Map settled (user pan/zoom): collapse any spiderfy, then recompute as usual.
const onMapSettle = () => {
    if (spiderfiedIds.value.size) spiderfiedIds.value = new Set();
    scheduleCollision();
};
// Click on empty map: tell the parent, and collapse a spiderfy if one is open.
const onMapClick = () => {
    hideContextMenu();
    emits('map-clicked');
    if (spiderfiedIds.value.size) {
        spiderfiedIds.value = new Set();
        scheduleCollision();
    }
};

// Right-click "Save place here": a lightweight context menu on the live map that
// opens the create-place dialog seeded at the clicked point.
const contextMenu = reactive({show: false, x: 0, y: 0, lat: 0, lng: 0});
const placeDialogOpen = ref(false);
const placePoint = ref(null);
const onMapContextMenu = (e) => {
    e.originalEvent?.preventDefault?.();
    contextMenu.x = e.containerPoint.x;
    contextMenu.y = e.containerPoint.y;
    contextMenu.lat = e.latlng.lat;
    contextMenu.lng = e.latlng.lng;
    contextMenu.show = true;
};
const hideContextMenu = () => { contextMenu.show = false; };
const onSavePlaceHere = () => {
    placePoint.value = {lat: contextMenu.lat, lng: contextMenu.lng};
    contextMenu.show = false;
    placeDialogOpen.value = true;
};
const onPlaceCreatedFromMap = () => { placeDialogOpen.value = false; };

const {copyToClipboard} = useClipboard();
const locationPolygons = ref(new L.FeatureGroup());

// ===== Places (geofence templates) drawn as named shapes under the devices =====
// Lazily fetched once the map is ready. Polygons/circles render in Leaflet's
// overlay pane, which sits below the marker pane, so devices stay on top.
const placesLayer = ref(new L.FeatureGroup());
// interactive:false so the decorative overlay never swallows a map background
// click (deselect / collapse cluster / hide context menu), matching the
// company-location overlay's convention.
// Light-gray, dotted outline (dashArray) so the geofence overlay reads as a
// soft boundary rather than a hard solid ring, matching the mobile app.
const PLACE_STYLE = {color: '#9E9E9E', weight: 2, dashArray: '2 6', lineCap: 'round', fillColor: '#9E9E9E', fillOpacity: 0.08, interactive: false};

// Build a Leaflet shape from a place: a polygon when it has >=3 vertices,
// otherwise a circle from centre + radius. Returns null if geometry is missing.
const buildPlaceShape = (place) => {
    const pts = (place.polygon_coordinates || [])
        .map((c) => [Number(c.latitude ?? c.lat), Number(c.longitude ?? c.lng ?? c.lon)])
        .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
    if (pts.length >= 3) return L.polygon(pts, PLACE_STYLE);
    const clat = Number(place.center_lat);
    const clon = Number(place.center_lon ?? place.center_lng);
    const radius = Number(place.radius);
    if (Number.isFinite(clat) && Number.isFinite(clon) && radius > 0) {
        return L.circle([clat, clon], {radius, ...PLACE_STYLE});
    }
    return null;
};

const drawPlaces = (places) => {
    placesLayer.value.clearLayers();
    for (const place of places) {
        const shape = buildPlaceShape(place);
        if (!shape) continue;
        const name = String(place.label || place.name || 'Place');
        // A permanent centred tooltip names the place right on its shape.
        shape.bindTooltip(name, {permanent: true, direction: 'center', className: 'place-label'});
        placesLayer.value.addLayer(shape);
    }
};

// Fetch all of the circle's places (paginated; small for a household) and draw
// them. Decorative overlay, so any failure is swallowed silently.
const fetchAndDrawPlaces = async () => {
    try {
        const all = [];
        let page = 1;
        let lastPage = 1;
        do {
            const res = await send('get', 'monitoring/geofence-templates', {page});
            const d = res?.data?.data;
            all.push(...(d?.data ?? []));
            lastPage = d?.meta?.last_page ?? d?.last_page ?? 1;
            page++;
        } while (page <= lastPage && page <= 10);
        drawPlaces(all);
    } catch (e) {
        // non-fatal
    }
};
const mapCenter = ref([47.41322, -1.219482]);
// True once the map has auto-fit to all devices on first load, so later data
// refreshes don't re-center and fight the user's manual pan/zoom.
const hasInitialFit = ref(false);

// A device's position, falling back to the API's last-known point. On first load
// devices carry last_known_lat/lng before MQTT has sent a live lat/lng, so the
// initial fit must use the fallback or it sees "no positions" and never fits.
const deviceLatLng = (d) => {
    // Number(), not a bare ===: Postgres numeric columns arrive over REST as
    // STRINGS ("0.00000000"), which never === 0, so a genuine no-fix sentinel
    // passed this guard and the initial fit framed the Gulf of Guinea. The MQTT
    // merge writes numbers over the same field mid-session, so both types are
    // live at once. hasPosition above already does it this way.
    const lat = Number(d?.lat ?? d?.last_known_lat);
    const lng = Number(d?.lng ?? d?.last_known_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat === 0 && lng === 0) return null; // null-island guard
    return [lat, lng];
};

// Fit the whole fleet into view exactly once on first load: centre on the
// centroid of all units and zoom so every one is visible. Retries harmlessly
// until BOTH the map and at least one position are ready, then locks (later
// data refreshes must not yank the map away from the user's pan/zoom).
const tryInitialFit = () => {
    if (hasInitialFit.value) return;
    const lmap = map.value?.leafletObject;
    if (!lmap) return;
    const pts = (props.allDevices || props.devices || [])
        .filter(isMappable)
        .map(deviceLatLng)
        .filter(Boolean);
    if (!pts.length) return;
    if (pts.length === 1) {
        lmap.setView(pts[0], 16);
    } else {
        lmap.fitBounds(L.latLngBounds(pts), {padding: [80, 80]});
    }
    hasInitialFit.value = true;
    scheduleCollision();
};
// Per-device throttle for live reverse-geocoding so frequent MQTT position
// ticks don't hammer (and get banned by) Nominatim.
const geocodeThrottle = {};
const GEOCODE_MIN_INTERVAL_MS = 30000;
const lastMqttUpdate = inject('lastMqttUpdate', ref(null));
const emits = defineEmits(['selectedMarker', 'map-clicked']);
const props = defineProps(['devices', 'allDevices', 'height', 'selectedDevice', 'zoomToDevice', 'nearestToDevice']);
const devicesInMotion = computed(() => {
    return props.devices.filter((device) => device['motion_state'] === 1 && device['isHighlighted'] === 1)
})
const mqttMessageTypes = {
    MOTION_STARTED: 2,
    MOTION_IN_PROGRESS: 3,
    MOTION_ENDED: 4,
}

// ========== Follow mode ==========
const trackedDeviceIds = reactive(new Set());
const showFollowingPanel = ref(false);
// The device whose "Share live location" dialog is open (null = closed).
const shareDevice = ref(null);
const openShare = (device) => { shareDevice.value = device; };

const followedDevicesWithPosition = computed(() => {
    const allDevices = props.allDevices || props.devices || [];
    return allDevices.filter(d => trackedDeviceIds.has(d.id) && isMappable(d));
});

const toggleFollow = (device) => {
    if (trackedDeviceIds.has(device.id)) {
        trackedDeviceIds.delete(device.id);
    } else {
        trackedDeviceIds.add(device.id);
        // Geocode address for followed device if missing. Not for an approximate
        // seed: labelling a point it never reported from would be misleading.
        if (!isApproximate(device) && !device.location && !isValidAddress(device.formatted_address) && device.lat && device.lng) {
            geocodeLatLng({lat: device.lat, lon: device.lng}).then(addr => {
                if (!isValidAddress(addr)) return;
                const idx = (props.allDevices || props.devices).findIndex(d => d.id === device.id);
                if (idx > -1) (props.allDevices || props.devices)[idx]['location'] = addr;
            });
        }
    }
};

const unfollowDevice = (deviceId) => {
    trackedDeviceIds.delete(deviceId);
    if (trackedDeviceIds.size === 0) showFollowingPanel.value = false;
};

const unfollowAll = () => {
    trackedDeviceIds.clear();
    showFollowingPanel.value = false;
};

// ========== Position animation ==========
const animationTimers = new Map();

const animatePosition = (deviceIndex, fromLat, fromLng, toLat, toLng) => {
    const deviceId = (props.allDevices || props.devices)[deviceIndex]?.id;
    if (!deviceId) return;

    // Cancel any running animation for this device
    if (animationTimers.has(deviceId)) {
        clearInterval(animationTimers.get(deviceId));
    }

    const steps = 10;
    const stepDuration = 50; // 10 steps × 50ms = 500ms
    let currentStep = 0;
    const latStep = (toLat - fromLat) / steps;
    const lngStep = (toLng - fromLng) / steps;

    const timer = setInterval(() => {
        currentStep++;
        const devices = props.allDevices || props.devices;
        if (currentStep >= steps) {
            devices[deviceIndex]['lat'] = toLat;
            devices[deviceIndex]['lng'] = toLng;
            clearInterval(timer);
            animationTimers.delete(deviceId);
        } else {
            devices[deviceIndex]['lat'] = fromLat + (latStep * currentStep);
            devices[deviceIndex]['lng'] = fromLng + (lngStep * currentStep);
        }
    }, stepDuration);

    animationTimers.set(deviceId, timer);
};

// ========== Basemap selector ==========
// Map = OSM street tiles, Satellite = Esri World Imagery. Choice persists per
// browser. NOTE: the on-map attribution control is removed in mapIsReady - OSM
// and Esri's tile terms require attribution, so this is only compliant once we
// switch to self-hosted tiles or a paid tile plan.
const BASEMAPS = {
    street: {
        label: 'Map',
        icon: 'mdi-map-outline',
        url: mapConfig.streetUrl,
        subdomains: 'abc',
    },
    satellite: {
        label: 'Satellite',
        icon: 'mdi-satellite-variant',
        url: mapConfig.satelliteUrl,
        subdomains: '',
    },
};
const mapType = ref(BASEMAPS[localStorage.getItem('go_map_type')] ? localStorage.getItem('go_map_type') : 'street');
const currentBasemap = computed(() => BASEMAPS[mapType.value] ?? BASEMAPS.street);
const setMapType = (type) => {
    if (!BASEMAPS[type]) return;
    mapType.value = type;
    try {
        localStorage.setItem('go_map_type', type);
    } catch (e) { /* private mode - keep in-memory only */ }
};

// ========== Movement trails ==========
const activeTrails = ref([]);
let trailIdCounter = 0;

const addTrail = (fromLat, fromLng, toLat, toLng, color) => {
    const trailId = ++trailIdCounter;
    const trail = {
        id: trailId,
        points: [[fromLat, fromLng], [toLat, toLng]],
        color: color || '#1976D2',
        opacity: 0.7,
        fading: false,
    };
    activeTrails.value.push(trail);

    // After 3 seconds, start fading
    setTimeout(() => {
        const idx = activeTrails.value.findIndex(t => t.id === trailId);
        if (idx > -1) {
            activeTrails.value[idx].fading = true;
            activeTrails.value[idx].opacity = 0.3;
        }
        // Remove after fade (500ms)
        setTimeout(() => {
            activeTrails.value = activeTrails.value.filter(t => t.id !== trailId);
        }, 500);
    }, 3000);
};

// ========== Recent-movement breadcrumb path (last 10 min) ==========
// Per device, the timestamped points it has been at recently, drawn under the
// markers as a black|white|black line so you can see where it just travelled.
// Rolling window: points older than 10 min are pruned (on append and, so a
// parked device's line still fades, on the 5s time tick via the computed).
const RECENT_WINDOW_MS = 10 * 60 * 1000;
const RECENT_MAX_POINTS = 240;
const recentPaths = ref({});

const deviceKey = (d) => d?.id ?? d?.device_id ?? d?.asset_id ?? d?.slno;

// Parse a server UTC-without-suffix timestamp to epoch ms; NaN if unparseable.
const tsToMs = (raw) => {
    if (raw == null) return NaN;
    let s = String(raw).trim();
    if (!s) return NaN;
    s = s.replace(' ', 'T');
    if (!s.endsWith('Z') && !/[+-]\d\d:?\d\d$/.test(s)) s += 'Z';
    return new Date(s).getTime();
};

// Record one point on a device's recent path. [tMs] is the fix time (from the
// device's last_update or the MQTT message); defaults to now for a live fix.
// Guards: valid finite coords; in-range lat/lng; clamp a future fix to now;
// ignore an out-of-order (older) observation; dedup ~8m jitter; age-prune +
// length-cap. Robust to both the API poll and MQTT feeding the same buffer.
const pushRecentPoint = (d, lat, lng, tMs) => {
    const id = deviceKey(d);
    if (id == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return;
    const now = Date.now();
    let t = Number.isFinite(tMs) ? tMs : now;
    if (!Number.isFinite(t)) t = now;
    if (t > now) t = now;
    const arr = recentPaths.value[id] || (recentPaths.value[id] = []);
    const last = arr[arr.length - 1];
    if (last) {
        if (t < last.t) return; // older observation than the tail: keep order
        const moved = Math.abs(last.lat - lat) >= 0.00007 || Math.abs(last.lng - lng) >= 0.00007;
        if (!moved) return; // same spot (parked jitter): nothing to add
        // Implausible jump (>50km between two live points) = a GPS glitch, not
        // travel; skip so the trail can't spike across the map.
        if (L.latLng(last.lat, last.lng).distanceTo(L.latLng(lat, lng)) > 50000) return;
    }
    arr.push({lat, lng, t});
    const cutoff = now - RECENT_WINDOW_MS;
    while (arr.length && arr[0].t < cutoff) arr.shift();
    if (arr.length > RECENT_MAX_POINTS) arr.splice(0, arr.length - RECENT_MAX_POINTS);
};

// Trails to draw, filtered to the live window. Re-evaluates on the 5s tick so a
// parked device's line ages out even without new fixes.
const recentTrailLines = computed(() => {
    void timeAgoTick.value;
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    // Only draw trails for devices currently pinned, so a device filtered out of
    // view or hidden from the map never leaves a marker-less line behind.
    const visible = new Set(props.devices.filter(isMappable).map((d) => String(deviceKey(d))));
    const out = [];
    for (const [id, arr] of Object.entries(recentPaths.value)) {
        if (!visible.has(id)) continue;
        const pts = arr.filter((p) => p.t >= cutoff).map((p) => [p.lat, p.lng]);
        if (pts.length >= 2) out.push({id, points: pts});
    }
    return out;
});

// ========== Compass direction ==========
const compassLabel = (heading) => {
    if (heading == null || heading === undefined) return '';
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(heading / 45) % 8];
};

const truncateAddress = (addr) => {
    if (!addr) return '';
    return addr.length > 50 ? addr.substring(0, 50) + '...' : addr;
};

// ========== Ticking time-ago ==========
const timeAgoTick = ref(0);
const timeAgoTimer = setInterval(() => {
    timeAgoTick.value++;
}, 5000);
const getTimeAgo = (lastMessageReceived) => {
    if (!lastMessageReceived) return '';
    const now = new Date();
    let tsStr = String(lastMessageReceived);
    if (!tsStr.endsWith('Z') && !tsStr.includes('+') && !tsStr.includes('T')) {
        tsStr = tsStr.replace(' ', 'T') + 'Z';
    }
    const last = new Date(tsStr);
    const diffMs = now - last;
    if (isNaN(diffMs) || diffMs < 0) return '';
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 5) return 'just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
}
const tickingTimeAgo = (lastMessageReceived) => {
    void timeAgoTick.value;
    return getTimeAgo(lastMessageReceived);
}

// ========== Existing helpers ==========
const getDeviceMarker = (deviceID) => {
    return markers.value.find((marker) => marker.name === `marker-${deviceID}`);
}
const highLightMarkers = (deviceIDs) => {
    props.devices.map((device) =>
        deviceIDs.includes(device.id) ? device.isHighlighted = true : device.isHighlighted = false
    );
}
const findNearestMarker = (selectedMarker) => {
    let minDistance = Infinity;
    let nearestMarker = null;

    markers.value.forEach((marker) => {
        if (marker.name !== selectedMarker.name && marker.latLng) {
            const distance = L.latLng(selectedMarker.latLng).distanceTo(L.latLng(marker.latLng));
            if (distance < minDistance) {
                minDistance = distance;
                nearestMarker = marker;
            }
        }
    });

    if (nearestMarker) {
        highLightMarkers([props.nearestToDevice.id, parseInt(nearestMarker.name.replace('marker-', ''))]);
        bounds.value = L.featureGroup([L.marker(selectedMarker.latLng), L.marker(nearestMarker.latLng)]).getBounds();
    }
}
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {icon: 'mdi-signal-off', color: '#dbdbdb'}
        case 1:
            return {icon: 'mdi-signal-cellular-outline', color: '#d70000'}
        case 2:
            return {icon: 'mdi-signal-cellular-1', color: '#ff9e01'}
        case 3:
            return {icon: 'mdi-signal-cellular-2', color: '#849e00'}
        case 4:
        case 5:
            return {icon: 'mdi-signal-cellular-3', color: '#099f00'}
    }
}
const battery = (device) => {
    const {icon, color} = batteryDisplay(device);
    return {icon, color};
}
// Phones show an exact percent ("58%"); hardware shows its real voltage ("3.70v").
const batteryText = (device) => {
    const b = batteryDisplay(device);
    return b.isPercent ? b.label : strOperations().formatVoltage(device.voltage);
}
const driverScoreColor = (score) => {
    if (score >= 8.0) return 'success'
    if (score >= 5.0) return 'warning'
    return 'error'
}
const copyText = (device) => {
    if (device && device.lat && device.lng) {
        copyToClipboard(`https://www.google.com/maps?q=${device.lat},${device.lng}`);
    } else if (device && isValidAddress(device.formatted_address)) {
        copyToClipboard(device.formatted_address);
    } else {
        copyToClipboard(`${device.lat}, ${device.lng}`);
    }
}
// deviceId is passed in rather than read from selectedMarker at call time: the
// geocode is queued behind an app-wide 1.1s Nominatim lane, so by the time it
// resolves the user may have selected someone else - and this writes to the
// database, so the address landed on the wrong device's row.
const updateGeocodedAddress = (address, deviceId) => {
    if (!deviceId) return;
    send("put", `devices/update-address`, {device: deviceId, address: address})
        .catch((errorResponse) => console.log(errorResponse));
}
const openStreetView = (device) => {
    window.open(
        `http://maps.google.com/maps?q=${device.lat},${device.lng}&layer=t&cbll=${device.lat},${device.lng}&cbp=12,0,0,0,0&output=classic`,
        "_blank"
    );
}
const openInGoogleMaps = (device) => {
    window.open(`https://www.google.com/maps?q=${device.lat},${device.lng}`, '_blank');
}
// "Save Place": jump to the Places page with this device's current location
// pre-loaded so the place dialog opens centered here (user just names + saves).
const saveAsPlace = (device) => {
    const lat = device.lat ?? device.last_known_lat;
    const lng = device.lng ?? device.last_known_lng;
    if (lat == null || lng == null) return;
    router.push({name: 'places', query: {lat: String(lat), lng: String(lng)}});
}
const contactPhone = (device) => {
    const phone = device?.driver_phone;
    if (typeof phone !== 'string') return null;
    const trimmed = phone.trim();
    return trimmed.length ? trimmed : null;
}
const callContact = (device) => {
    const phone = contactPhone(device);
    if (phone) window.location.href = `tel:${phone}`;
}
const messageContact = (device) => {
    const phone = contactPhone(device);
    if (phone) window.location.href = `sms:${phone}`;
}
const openDeviceActivity = (device) => {
    // Deep-link to the Activity feed pre-filtered to this member/tracker. The key
    // mirrors Activity.vue's memberKeyOf: the owning member if set, else the asset.
    const key = device?.member_user_id != null
        ? `u${device.member_user_id}`
        : `a${device?.asset_id}`;
    router.push({name: 'activity', query: {focus: key}});
}
const openDeviceAlerts = () => {
    // Notifications were merged into Activity; go straight there (the
    // /notifications redirect still works, but this avoids the extra hop).
    window.open('/activity', '_blank');
}
const checkLastMessage = () => {
    const now = new Date();
    const allDevices = props.allDevices || props.devices;

    allDevices.forEach((device, index) => {
        let ts = String(device.last_message_received || '');
        if (ts && !ts.endsWith('Z') && !ts.includes('+') && !ts.includes('T')) {
            ts = ts.replace(' ', 'T') + 'Z';
        }
        const lastReceived = new Date(ts);
        const diffInMinutes = (now - lastReceived) / 1000 / 60;
        if (!isNaN(diffInMinutes) && diffInMinutes > 3 && device.motion_state !== 0) {
            allDevices[index]['isHighlighted'] = 0;
            allDevices[index]['motion_state'] = 0;
            allDevices[index]['status_color'] = 'amber';
            // A moving device that went quiet for 3 min is parked, not unknown.
            // Deleting fleet_status here handed the row to the 30-min freshness
            // fallback, which then called it Offline (see subjectStatusOf).
            allDevices[index]['fleet_status'] = 'parked';
        }
        // Past the server's 30h non-reporting cutoff the row is Offline
        // (subjectStatusOf's isReporting gate); mirror that onto fleet_status so
        // the Offline filter chip (isNonReporting trusts fleet_status) agrees in
        // a tab left open past the cutoff with no refetch. sharing_off already
        // arrives as 'offline' from the API and outranks this everywhere.
        if (!isNaN(diffInMinutes) && diffInMinutes > NON_REPORTING_HOURS * 60
            && device.fleet_status && device.fleet_status !== 'offline') {
            allDevices[index]['fleet_status'] = 'offline';
        }
    });
}
const selectMarker = (device) => {
    // Clicking a marker opens its popup; mark it open so Recent-trips fetches
    // even if the popupopen event isn't forwarded on this path.
    openPopupId.value = device.id;
    // Center on the tapped device and zoom in enough to see the area - but never
    // zoom out if the user is already closer in.
    const lmap = map.value?.leafletObject;
    if (lmap && device.lat != null && device.lng != null) {
        const target = Math.max(lmap.getZoom(), 16);
        lmap.setView([device.lat, device.lng], target, {animate: true});
    }
    if (selectedMarker.value?.id !== device.id) {
        selectedMarker.value = device;
        emits('selectedMarker', device);
        // Re-anchor: the newly selected marker is top priority, so recompute so
        // it returns to its true point and others declutter around it (a
        // spiderfied member instead stays out on its ring).
        scheduleCollision();
    }
}

// Run every 30 seconds to detect 3-minute staleness
const lastMessageCheckTimer = setInterval(checkLastMessage, 30 * 1000);

const mapIsReady = () => {
    mapState.value = true;
    // Remove the attribution control entirely (product decision). NOTE: OSM and
    // Esri's tile terms require attribution - this is only acceptable once we move
    // to self-hosted tiles or a paid tile plan whose licence permits it.
    map.value.leafletObject.attributionControl.remove();
    if (locationPolygons.value) {
        map.value.leafletObject.addLayer(locationPolygons.value);
    }
    // Draw the circle's saved Places as named shapes beneath the device markers.
    map.value.leafletObject.addLayer(placesLayer.value);
    fetchAndDrawPlaces();
    // Devices may have loaded before the map was ready, in which case the
    // devices watcher could not fit yet. Do the one-time fit now.
    tryInitialFit();
    // Recompute marker declutter after pan/zoom settles (covers fitBounds too),
    // and collapse any open spiderfy since the screen layout changed.
    map.value.leafletObject.on('moveend zoomend', onMapSettle);
    // Right-click anywhere on the map to save a place at that spot.
    map.value.leafletObject.on('contextmenu', onMapContextMenu);
    map.value.leafletObject.on('movestart', hideContextMenu);
    scheduleCollision();
}

// ========== Fit all devices (exposed to parent) ==========
const fitAllDevices = () => {
    const allDevices = props.allDevices || props.devices || [];
    const validBounds = allDevices
        .filter(isMappable)
        .map(d => [d.lat, d.lng]);
    if (validBounds.length && map.value?.leafletObject) {
        map.value.leafletObject.fitBounds(L.latLngBounds(validBounds), {padding: [50, 50]});
    }
};

defineExpose({fitAllDevices});

// ========== Watchers ==========
watch(() => props.selectedDevice, async (device) => {
    if (device) {
        selectedMarker.value = device;
        // Selecting pins the device, so force a recompute to lift it out of any
        // cluster, then wait for it to (re)mount before opening its popup.
        recomputeCollisions();
        await nextTick();
        getDeviceMarker(device.id)?.leafletObject?.openPopup();
        // Never geocode an activation seed: the point is where it will be, not
        // where it reported from, so persisting an address for it (the PUT in
        // updateGeocodedAddress) would stamp a real-looking location on a device
        // with no fix.
        if (!isApproximate(device) && !isValidAddress(device.formatted_address)) {
            await geocodeLatLng({'lat': device.lat, 'lon': device.lng})
                .then((response) => {
                        if (!isValidAddress(response)) return;
                        // props.devices is the FILTERED list while the selection
                        // comes from the unfiltered one, so this can legitimately
                        // miss; -1 used to throw on the assignment below.
                        const deviceIndex = props.devices.findIndex((item) => sameSerial(item.slno, device.slno));
                        if (deviceIndex > -1) {
                            props.devices[deviceIndex]['formatted_address'] = response;
                        }
                        // Only touch the popup if it is still this device's.
                        if (sameSerial(selectedMarker.value?.slno, device.slno)) {
                            selectedMarker.value['formatted_address'] = response;
                        }
                        updateGeocodedAddress(response, device.id)
                    }
                )
        }
    }
});

watch(() => props.zoomToDevice, (device) => {
    if (!device) return;
    const marker = getDeviceMarker(device.id);
    if (marker) {
        map.value.leafletObject.setView(marker.latLng, 18);
    } else if (device.lat != null && device.lng != null) {
        // Hidden inside a cluster: zoom to its coordinates - the zoom-in then
        // breaks the cluster apart and the marker reappears.
        map.value?.leafletObject?.setView([device.lat, device.lng], 18);
    }
});

watch(() => props.nearestToDevice, (device) => {
    if (!device) return;
    // No marker when the target is clustered - skip rather than throw.
    const marker = getDeviceMarker(device.id);
    if (marker) findNearestMarker(marker);
});

watch(() => props.devices, (devices) => {
    // Feed the recent-movement path from the API/poll data too - timestamped by
    // the device's real last update, so a stale position ages out correctly -
    // and track which devices are still present to prune departed ones below.
    const presentIds = new Set();
    props.devices.forEach((device) => {
        if (!device.motion_state) {
            device.isHighlighted = false;
        }
        const id = deviceKey(device);
        if (id != null) presentIds.add(String(id));
        if (device.lat != null && device.lng != null) {
            pushRecentPoint(
                device, Number(device.lat), Number(device.lng),
                tsToMs(device.last_message_received ?? device.last_updated ?? device.last_seen),
            );
        }
    });
    // Drop paths for devices no longer on the map (removed / sharing off / left
    // the company), so a stale line isn't drawn and the buffer stays bounded.
    for (const key of Object.keys(recentPaths.value)) {
        if (!presentIds.has(key)) delete recentPaths.value[key];
    }
    bounds.value = props.devices
        .filter(isMappable)
        .map((device) => [device.lat, device.lng]);
    // Fit the map to all devices only on the first load. Re-fitting on every
    // data refresh (MQTT token rotation, company reload, polling) would yank
    // the map away from wherever the user has panned or zoomed. The user can
    // re-fit any time with the fit-all-devices button.
    tryInitialFit();
    scheduleCollision();
}, {deep: true});

watch(lastMqttUpdate, async (update) => {
    if (!update) return;
    const slno = update.slno;
    const location = update.data;
    const allDevices = props.allDevices || props.devices;

    // Coerce AND trim both sides: slno arrives as a number (server JSON), a
    // string (topic split), or device.slno's type, and the MQTT payload carries
    // the char(16) blank padding the REST list does not. Either mismatch
    // silently freezes that device's marker while data keeps flowing.
    let deviceIndex = allDevices.findIndex((device) => sameSerial(device.slno, slno));
    if (deviceIndex > -1) {
        const device = allDevices[deviceIndex];

        // Only update motion state for known motion message types
        const motionMessage = [mqttMessageTypes.MOTION_STARTED, mqttMessageTypes.MOTION_IN_PROGRESS, mqttMessageTypes.MOTION_ENDED].includes(location.message_type);
        if (motionMessage) {
            const ended = location.message_type === mqttMessageTypes['MOTION_ENDED'];
            allDevices[deviceIndex]['motion_state'] = ended ? 0 : 1;
            allDevices[deviceIndex]['isHighlighted'] = ended ? 0 : 1;
            // Capture the moment movement ended (the "arrival" time). While moving
            // we clear it so the presence label reads "Moving" not a stale arrival.
            allDevices[deviceIndex]['motion_ended_at'] = ended ? location.last_message_received : null;
            // Mirror for the movement start so the pill can show "Moving for X" on
            // the live path, not just after an API poll. Stamp it on MOTION_STARTED,
            // clear on MOTION_ENDED, and leave it untouched on MOTION_IN_PROGRESS so
            // the original start survives the whole trip. A dropped MOTION_STARTED
            // then degrades to the plain word, never a stale previous-trip start.
            if (location.message_type === mqttMessageTypes.MOTION_STARTED) {
                allDevices[deviceIndex]['motion_started_at'] = location.last_message_received;
            } else if (ended) {
                allDevices[deviceIndex]['motion_started_at'] = null;
            }
        }
        // Rewrite fleet_status from the live packet rather than deleting it
        // (parity with the Go app's device_service MQTT handler). A packet just
        // arrived, so the device is present: "offline" is no longer true, and a
        // missing fleet_status would hand subjectStatusOf to its 30-min freshness
        // fallback, which greys a parked tracker as Offline between heartbeats.
        // Motion messages carry the state; anything else (heartbeat, alert) only
        // lifts offline/unknown to parked and leaves moving/idling/parked alone.
        if (motionMessage) {
            const ended = location.message_type === mqttMessageTypes['MOTION_ENDED'];
            const speed = location.valid !== false
                ? (Number(location.gps_speed) || 0)
                : (Number(device.gps_speed_raw ?? device.gps_speed) || 0);
            allDevices[deviceIndex]['fleet_status'] = ended ? 'parked' : (speed > 2 ? 'moving' : 'idling');
        } else if (!device.fleet_status || device.fleet_status === 'offline') {
            allDevices[deviceIndex]['fleet_status'] = 'parked';
        }
        // A parked phone that turned up somewhere new without a trip: the
        // gateway restamps motion_ended_at (the "Here for X" anchor) on that
        // packet, which is not a motion message. Take it, or the dwell keeps
        // counting from the previous stop until the page reloads.
        if (!motionMessage && location.motion_ended_at) {
            allDevices[deviceIndex]['motion_ended_at'] = location.motion_ended_at;
        }

        // Always update timestamps and voltage/signal/battery
        allDevices[deviceIndex]['last_updated_ago'] = location.last_updated_ago;
        allDevices[deviceIndex]['last_message_received'] = location.last_message_received;
        const dev = allDevices[deviceIndex];
        allDevices[deviceIndex]['voltage'] = dev.battery2_status && !dev.battery1_status
            ? location.voltage2 : location.voltage1;
        allDevices[deviceIndex]['voltage2'] = location.voltage2;
        allDevices[deviceIndex]['signal_bars'] = location.signal_bars;
        allDevices[deviceIndex]['battery_bars'] = location.battery_bars;
        // Phone battery is a percent carried on voltage1 (percent = voltage1/50).
        // Keep it live off MQTT voltage1 for phone devices (identified explicitly
        // via is_phone) rather than showing a stale value.
        // 0 mV from a phone is "battery unknown" (the uplink encodes a null OS
        // reading as 0; a phone at a true 0% is off), so keep the REST value
        // rather than paint 0% until the next heartbeat.
        if (isPhoneDevice(dev)) {
            if (Number(location.voltage1) > 0) {
                allDevices[deviceIndex]['battery_percent'] =
                    Math.max(0, Math.min(100, Math.round(location.voltage1 / 50)));
            }
        } else {
            // A hardware vehicle with a configured voltage range also carries a
            // percent, derived server-side from its primary voltage. Recompute it
            // from the same live voltage that just updated the "12.5v" text, or
            // the row shows a fresh voltage beside a page-load percent that no
            // longer matches it (BMW: 12.5v next to 54%, which is 12.3v).
            const pct = vehicleBatteryPercent(dev, location.voltage1, location.voltage2);
            if (pct !== undefined) allDevices[deviceIndex]['battery_percent'] = pct;
        }
        if (location.driver) {
            allDevices[deviceIndex]['driver_photo'] = location.driver['driver_photo'];
            allDevices[deviceIndex]['driver_last_name'] = location.driver['driver_last_name'];
            allDevices[deviceIndex]['driver_first_name'] = location.driver['driver_first_name'];
        }

        // Only update position/speed/heading when GPS fix is valid
        if (location.valid !== false) {
            const oldLat = device.lat;
            const oldLng = device.lng;
            const newLat = location.lat;
            const newLng = location.lng;

            // A real fix replaces the activation seed: clear the seed/never-
            // reported flags so the pin un-fades and reads its live status - but
            // only when the packet carries usable coordinates, so a coordless or
            // 0,0 "valid" packet can't un-fade it or strand it at null-island.
            const _nl = Number(newLat);
            const _ng = Number(newLng);
            if (Number.isFinite(_nl) && Number.isFinite(_ng) && !(_nl === 0 && _ng === 0)) {
                allDevices[deviceIndex]['approximate'] = false;
                allDevices[deviceIndex]['never_reported'] = false;
            }

            // Record the point for the recent-movement breadcrumb path (deduped
            // + age-pruned inside). Every valid fix, not just >3m moves.
            pushRecentPoint(allDevices[deviceIndex], newLat, newLng);

            // Check distance for animation threshold (>3m)
            const distance = oldLat && oldLng ? L.latLng(oldLat, oldLng).distanceTo(L.latLng(newLat, newLng)) : 0;

            if (distance > 3) {
                // The device moved, so the server-resolved Safe Zone is now stale
                // (we can't recompute zone membership client-side). Clear it so the
                // place falls back to the fresh live address until the next API
                // poll re-resolves the zone. Parked jitter (<=3m) keeps the zone.
                allDevices[deviceIndex]['current_zone'] = null;

                // Add movement trail
                const trailColor = device.motion_state ? '#1976D2' : '#9E9E9E';
                addTrail(oldLat, oldLng, newLat, newLng, trailColor);

                // Animate position smoothly
                animatePosition(deviceIndex, oldLat, oldLng, newLat, newLng);
            } else {
                allDevices[deviceIndex]['lat'] = newLat;
                allDevices[deviceIndex]['lng'] = newLng;
            }

            allDevices[deviceIndex]['heading'] = location.heading;
            allDevices[deviceIndex]['gps_speed_raw'] = Number(location.gps_speed) || 0;
            // Keep gps_speed a RAW number (km/h x2) so comparisons (fleetStatus,
            // motion pills) work; the display sites format it via unitifySpeed.
            allDevices[deviceIndex]['gps_speed'] = Number(location.gps_speed) || 0;
            // The gateway now sends a resolved speed alongside the raw one.
            // Assigned unconditionally: assigning only when present would leave
            // a REST-loaded speed_mph pinned while gps_speed kept ticking,
            // freezing the displayed number. Nulling re-engages the fallback.
            allDevices[deviceIndex]['speed_mph'] =
                location.speed_mph === undefined || location.speed_mph === null
                    ? null
                    : Number(location.speed_mph);

            // Geocode selected/followed devices, throttled per device so we
            // don't hit Nominatim on every tick; others just show coordinates.
            if (selectedMarker.value?.slno === slno || trackedDeviceIds.has(device.id)) {
                const nowTs = Date.now();
                if (nowTs - (geocodeThrottle[device.id] || 0) > GEOCODE_MIN_INTERVAL_MS) {
                    geocodeThrottle[device.id] = nowTs;
                    geocodeLatLng({'lat': newLat, 'lon': newLng}).then((response) => {
                        if (isValidAddress(response)) allDevices[deviceIndex]['location'] = response;
                    });
                }
            } else {
                allDevices[deviceIndex]['location'] = `${newLat}, ${newLng}`;
            }

            // Auto-adjust map for followed devices
            if (trackedDeviceIds.has(device.id) && map.value?.leafletObject) {
                const mapBounds = map.value.leafletObject.getBounds();
                if (!mapBounds.contains([newLat, newLng])) {
                    if (trackedDeviceIds.size === 1) {
                        map.value.leafletObject.panTo([newLat, newLng]);
                    } else {
                        const followedBounds = followedDevicesWithPosition.value.map(d => [d.lat, d.lng]);
                        if (followedBounds.length) {
                            map.value.leafletObject.fitBounds(L.latLngBounds(followedBounds), {padding: [50, 50]});
                        }
                    }
                }
            }
        }
    }
});

onBeforeUnmount(() => {
    clearInterval(lastMessageCheckTimer);
    clearInterval(timeAgoTimer);
    if (collideTimer) clearTimeout(collideTimer);
    map.value?.leafletObject?.off('moveend zoomend', onMapSettle);
    // Clean up animation timers
    animationTimers.forEach(timer => clearInterval(timer));
    animationTimers.clear();
})
</script>

<style>

/* --- Cluster badge --- */
.cluster-marker {
    background: none !important;
    border: none !important;
}

.map-context-menu {
    position: absolute;
    z-index: 1000;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    min-width: 160px;
}

.map-context-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 10px 14px;
    font-size: 14px;
    color: #26235A;
    background: transparent;
    border: none;
    cursor: pointer;
    white-space: nowrap;
}

.map-context-item:hover {
    background: rgba(38, 35, 90, 0.08);
}

.cluster-badge {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 15px;
    color: #ffffff;
    background: #26235A;
    border: 3px solid #ffffff;
    box-shadow: 0 0 0 1.5px rgba(80, 80, 80, 0.85), 0 2px 5px rgba(0, 0, 0, 0.35);
    cursor: pointer;
    transition: transform 120ms ease;
}

.cluster-badge:hover {
    transform: scale(1.08);
}

/* --- Address label styles --- */
.address-label-anchor {
    background: none !important;
    border: none !important;
}

.address-label {
    background: rgba(38, 35, 90, 0.9);
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    line-height: 1.4;
    white-space: nowrap;
    text-align: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    max-width: 200px;
}

.address-label-name {
    font-weight: 600;
    font-size: 11px;
    margin-bottom: 1px;
}

.address-label-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 10px;
}

.address-label-speed {
    font-weight: 600;
}

.address-label-speed.moving {
    color: #42A5F5;
}

.address-label-speed.idle {
    color: #FFA726;
}

.address-label-speed.parked {
    color: #BDBDBD;
}

.address-label-time {
    color: rgba(255, 255, 255, 0.6);
}

.address-label-address {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
}

.address-label-place-ic {
    color: rgba(255, 255, 255, 0.85);
    margin-right: 2px;
    vertical-align: text-bottom;
}

/* --- Following panel --- */
.following-panel {
    position: absolute;
    top: 72px;
    left: 8px;
    z-index: 1001;
}

/* Push the Leaflet zoom (+/-) control down so it clears the fleet-filter chip
   that sits at the top-left of the map. */
.devices-live-map .leaflet-top.leaflet-left {
    margin-top: 52px;
}

/* Basemap (Map / Satellite) selector - bottom-left, clear of the top filter
   chips and the bottom-right fit/fullscreen controls. */
.basemap-control {
    position: absolute;
    bottom: 12px;
    left: 12px;
    z-index: 1000;
}

.following-panel-list {
    background: white;
    border-radius: 8px;
    padding: 4px;
    margin-top: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    max-height: 200px;
    overflow-y: auto;
    min-width: 180px;
}

.following-panel-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 6px;
    font-size: 12px;
}

.following-panel-name {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 130px;
}

/* --- Popup redesign styles --- */
.popup-container {
    font-size: 13px;
    line-height: 1.4;
}

.popup-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.popup-photo-wrapper {
    position: relative;
    flex-shrink: 0;
}

.popup-photo {
    flex-shrink: 0;
    border: 1px solid #e0e0e0;
}

.popup-driver-overlay {
    position: absolute;
    right: -4px;
    bottom: -4px;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.popup-header-info {
    flex: 1;
    min-width: 0;
}

.popup-asset-label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    text-transform: capitalize;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.popup-subtitle {
    font-size: 11px;
    color: #999;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.popup-header-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.popup-status-strip {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding: 4px 8px;
    background: #f5f5f5;
    border-radius: 6px;
}

.popup-motion-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    flex-shrink: 0;
}

.popup-speed {
    font-size: 12px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

.popup-direction {
    font-size: 11px;
    font-weight: 500;
    color: #666;
    margin-left: 2px;
}

.popup-status-icons {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

.popup-voltage {
    font-size: 11px;
    color: #666;
    margin-left: 2px;
}

/* Fuel reading. Colour is bound inline (it varies with the level), so this
   only owns layout and weight. Bold because the low states have to carry
   across a busy strip on their colour alone. */
.popup-fuel {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 2px;
}

.popup-info-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    font-size: 12px;
    color: #444;
}

.popup-location-row {
    align-items: flex-start;
}

.popup-location-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.popup-zone-name {
    font-weight: 600;
    color: #2e7d32;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.popup-location-address {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}

.popup-presence {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-top: 2px;
    font-size: 11.5px;
    font-weight: 600;
    color: #37474f;
}

.popup-location-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
    margin-top: 1px;
}

.popup-inline-icon {
    cursor: pointer;
    opacity: 0.5;
    color: #666;
}

.popup-inline-icon:hover {
    opacity: 1;
    color: #1565C0;
}

.popup-actions {
    display: flex;
    justify-content: space-around;
    padding-top: 4px;
}

.popup-action-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    cursor: pointer;
}

.popup-action-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
}

.popup-action-circle:hover {
    opacity: 0.85;
}

.popup-action-label {
    font-size: 10px;
    color: #666;
    white-space: nowrap;
}

/* Place name centred on its geofence shape: a bare label (no tooltip box/arrow),
   with a soft halo so it stays legible over map tiles and the shape fill. */
.place-label.leaflet-tooltip {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
    color: #424242;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 1px 2px rgba(255, 255, 255, 0.9);
    pointer-events: none;
}

.place-label.leaflet-tooltip::before {
    display: none;
}
</style>
